import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { generationAPI } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { shirtSizeVariant } from './constant';
import { Helmet } from 'react-helmet';

const MyVideos = () => {
  const { isAuthenticated, user } = useAuth();
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('image');

  const fetchMyVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // If we have a videoId in URL, fetch that specific video (public access)
      if (videoId) {
        try {
          const response = await generationAPI.getPublicVideo(videoId);
          console.log('Fetched public video:', response.data);
          console.log('Video URL:', response.data.watermarked_video_url);
          setSelectedVideo(response.data);
          setLoading(false);
          return;
        } catch (err) {
          console.error('Error fetching public video:', err);
          setError('Video not found or no longer available.');
          setLoading(false);
          return;
        }
      }
      
      // Otherwise, fetch user's videos (requires authentication)
      if (!isAuthenticated) {
        setError('Please log in to view your videos');
        setLoading(false);
        return;
      }

      const response = await generationAPI.listVideos();
      const fetchedVideos = response.data.videos || [];
      setVideos(fetchedVideos);
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Failed to load your videos. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, videoId]);

  useEffect(() => {
    fetchMyVideos();
  }, [fetchMyVideos]);

  useEffect(() => {
    // Clear selected video when navigating to base /my-videos without ID
    if (!videoId && selectedVideo) {
      setSelectedVideo(null);
      setActiveTab('image');
    }
  }, [videoId, selectedVideo]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#4CAF50';
      case 'processing':
        return '#FF9800';
      case 'failed':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  };

  const handleCardClick = (video) => {
    setSelectedVideo(video);
    setActiveTab('image');
    navigate(`/my-videos/${video.task_id}`);
  };

  const handleCloseDetail = () => {
    setSelectedVideo(null);
    setActiveTab('image');
    navigate('/my-videos');
  };

  const generateCaption = (video) => {
    return `Check out my AI-generated fashion video! 🎬✨\n\n` +
           `Create your own at Pakainow.AI!\n\n` +
           `#PakaiNowAI #MyAITee #WearTheFuture #AIFashion #CustomTShirt #FashionTech`;
  };

  const handleShareImage = (platform, video) => {
    const caption = generateCaption(video);
    // Use backend share URL for proper Open Graph meta tags with task_id
    const shareUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/share/video/${video.task_id}`;
    const directImageUrl = video.watermarked_image_url;
    
    if (platform === 'copy') {
      const shareText = `${caption}\n\n${shareUrl}`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Shareable URL and caption copied to clipboard!');
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Shareable URL and caption copied to clipboard!');
      });
    } else if (platform === 'whatsapp') {
      const text = encodeURIComponent(`${caption}\n\n${shareUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    } else if (platform === 'facebook') {
      // Use the backend share URL for proper Open Graph tags with caption
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`, '_blank');
    } else if (platform === 'twitter') {
      const text = encodeURIComponent(caption);
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'instagram') {
      const shareText = `${caption}\n\nImage: ${directImageUrl}\n\n📱 Download the image and share it on Instagram!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the image and paste the caption when sharing on Instagram.');
      });
    }
  };

  const handleShare = (platform, video) => {
    const caption = generateCaption(video);
    // Use backend share URL for proper Open Graph meta tags with task_id
    const shareUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/share/video/${video.task_id}`;
    const directVideoUrl = video.watermarked_video_url;
    
    if (platform === 'copy') {
      const shareText = `${caption}\n\n${shareUrl}`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Shareable URL and caption copied to clipboard!');
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Shareable URL and caption copied to clipboard!');
      });
    } else if (platform === 'whatsapp') {
      const text = encodeURIComponent(`${caption}\n\n${shareUrl}`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
    } else if (platform === 'facebook') {
      // Use the backend share URL for proper Open Graph tags with caption
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`, '_blank');
    } else if (platform === 'twitter') {
      const text = encodeURIComponent(caption);
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'tiktok') {
      const shareText = `${caption}\n\nVideo: ${directVideoUrl}\n\n📱 Download the video and share it on TikTok!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the video and paste the caption when sharing on TikTok.');
      });
    } else if (platform === 'instagram') {
      const shareText = `${caption}\n\nVideo: ${directVideoUrl}\n\n📱 Download the video and share it on Instagram!`;
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Caption copied! Download the video and paste the caption when sharing on Instagram.');
      });
    }
  };

  if (loading) {
    return (
      <div className="my-videos-container loading">
        <div className="loading-spinner"></div>
        <p>Loading your videos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-videos-container error">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchMyVideos} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Video Detail Modal
  if (selectedVideo) {
    const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6 };
    let sizeQuantities = {};
    
    try {
      if (selectedVideo.size_detection_data) {
        const sizeData = typeof selectedVideo.size_detection_data === 'string' 
          ? JSON.parse(selectedVideo.size_detection_data) 
          : selectedVideo.size_detection_data;
        
        if (sizeData && sizeData.size_quantities) {
          sizeQuantities = sizeData.size_quantities;
        }
      }
    } catch (e) {
      console.error('Failed to parse size data:', e);
    }

    return (
      <div className="upload-section">
        {/* Show back button only if user is authenticated and owns videos */}
        {isAuthenticated && videos.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <button onClick={handleCloseDetail} className="btn btn-outline">
              ← Back to My Videos
            </button>
          </div>
        )}

        <h2 style={{ fontSize: '24px', marginBottom: '10px', textAlign: 'center' }}>
          {user && selectedVideo.user_id === user.id ? 'Your Generated Video' : 'AI-Generated Fashion Video'}
        </h2>
        <p style={{ textAlign: 'center', color: '#718096', marginBottom: '30px' }}>
          Created: {formatDate(selectedVideo.created_at)}
        </p>

        <div className="generate-section">
          <div>
            <div style={{
              background: '#f7fafc',
              padding: '40px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '30px',
                gap: '10px',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <button
                  onClick={() => setActiveTab('image')}
                  style={{
                    padding: '12px 32px',
                    background: activeTab === 'image' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: activeTab === 'image' ? 'white' : '#4a5568',
                    border: 'none',
                    borderBottom: activeTab === 'image' ? '3px solid transparent' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s',
                    marginBottom: '-2px'
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== 'image') {
                      e.target.style.background = '#f7fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== 'image') {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  🖼️ Image
                </button>
                <button
                  onClick={() => setActiveTab('video')}
                  disabled={!selectedVideo.watermarked_video_url && !selectedVideo.video_url}
                  style={{
                    padding: '12px 32px',
                    background: activeTab === 'video' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: activeTab === 'video' ? 'white' : '#4a5568',
                    border: 'none',
                    borderBottom: activeTab === 'video' ? '3px solid transparent' : '3px solid transparent',
                    borderRadius: '8px 8px 0 0',
                    cursor: (selectedVideo.watermarked_video_url || selectedVideo.video_url) ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s',
                    marginBottom: '-2px',
                    opacity: (selectedVideo.watermarked_video_url || selectedVideo.video_url) ? 1 : 0.5
                  }}
                  onMouseOver={(e) => {
                    if (activeTab !== 'video' && (selectedVideo.watermarked_video_url || selectedVideo.video_url)) {
                      e.target.style.background = '#f7fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeTab !== 'video') {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  🎬 Video
                </button>
              </div>

              {/* Image Preview */}
              {activeTab === 'image' && selectedVideo.watermarked_image_url && (
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  margin: '0 auto 30px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  background: '#fff'
                }}>
                  <img
                    src={selectedVideo.watermarked_image_url}
                    alt="Generated Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              )}

              {/* Video player */}
              {activeTab === 'video' && (selectedVideo.watermarked_video_url || selectedVideo.video_url) && (
                <div style={{
                  width: '100%',
                  maxWidth: '600px',
                  margin: '0 auto 20px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <video
                    key={selectedVideo.watermarked_video_url || selectedVideo.video_url}
                    src={selectedVideo.watermarked_video_url || selectedVideo.video_url}
                    controls
                    playsInline
                    preload="metadata"
                    crossOrigin="anonymous"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      backgroundColor: '#000'
                    }}
                    onError={(e) => {
                      console.error('Video playback error:', e);
                      console.error('Video URL:', selectedVideo.watermarked_video_url || selectedVideo.video_url);
                      console.error('Error details:', e.target.error);
                    }}
                    onLoadedMetadata={() => {
                      console.log('Video metadata loaded successfully');
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {activeTab === 'video' && !selectedVideo.watermarked_video_url && !selectedVideo.video_url && (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#6b7280', 
                  padding: '3rem' 
                }}>
                  <p style={{ fontSize: '18px', marginBottom: '10px' }}>🎬 Video not available</p>
                  <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                    {selectedVideo.status === 'processing' 
                      ? 'Video is still being processed. Please check back in a few minutes.'
                      : selectedVideo.status === 'failed'
                      ? 'Video generation failed. Please try creating a new video.'
                      : 'This video has not been generated yet.'}
                  </p>
                  {console.log('Video status:', selectedVideo.status)}
                  {console.log('Video data:', selectedVideo)}
                </div>
              )}
            </div>

            {/* Download and Buy Now Section */}
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              marginBottom: '30px',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  const url = activeTab === 'image' 
                    ? selectedVideo.watermarked_image_url 
                    : (selectedVideo.watermarked_video_url || selectedVideo.video_url);
                  if (!url) {
                    alert(`${activeTab === 'image' ? 'Image' : 'Video'} not available for download`);
                    return;
                  }
                  
                  try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = downloadUrl;
                    a.download = `pakainow-${activeTab}-${Date.now()}.${activeTab === 'image' ? 'jpg' : 'mp4'}`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Error downloading:', error);
                    window.open(url, '_blank');
                  }
                }}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                {activeTab === 'image' ? '🖼️ Download Image' : '🎬 Download Video'}
              </button>

              {selectedVideo.shop_url && selectedVideo.person_count > 0 && (
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    // Check if video belongs to current user
                    const isOwnVideo = user && selectedVideo.user_id === user.id;
                    
                    if (!isOwnVideo) {
                      // Redirect to homepage with shirt pre-selected
                      if (selectedVideo.shirt_product_id) {
                        // Store the selected shirt in localStorage so the home page can pick it up
                        localStorage.setItem('preselectedShirtId', selectedVideo.shirt_product_id);
                        navigate('/');
                      } else {
                        // If no shirt product ID, just go to homepage
                        navigate('/');
                      }
                      return;
                    }
                    
                    // For own videos, proceed with shop URL
                    let url = selectedVideo.shop_url;
                    let sizeQuantities = {};
                    
                    // Parse size quantities from size detection data
                    if (selectedVideo.size_detection_data) {
                      try {
                        console.log('Raw size data:', selectedVideo.size_detection_data);
                        const sizeData = JSON.parse(selectedVideo.size_detection_data);
                        console.log('Parsed size data:', sizeData);
                        sizeQuantities = sizeData.size_quantities;
                      } catch (e) {
                        console.error('Failed to parse size data:', e);
                      }
                    }
                    // Sort sizes and add to URL
                    const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6 };
                    
                    // Find size with highest quantity for display parameters
                    let maxQty = 0;
                    let displaySize = 'M';
                    console.log('Size quantities for URL:', sizeQuantities);
                    Object.entries(sizeQuantities).forEach(([size, qty]) => {
                      if (qty > maxQty) {
                        maxQty = qty;
                        displaySize = size;
                      } else if (qty === maxQty && sizeOrder[size] > sizeOrder[displaySize]) {
                        // If quantities are equal, prefer smaller size
                        displaySize = size;
                      }
                    });

                    // Add individual size quantities to URL
                    Object.entries(sizeQuantities)
                      .sort((a, b) => (sizeOrder[a[0]] || 99) - (sizeOrder[b[0]] || 99))
                      .forEach(([size, qty]) => {
                        if (qty > 0) {
                          url += `&${size.toLowerCase()}=${qty}`;
                        }
                      });

                    console.log('Size quantities for URL:', maxQty);
                    console.log('Display size for URL:', displaySize);

                    // Add custom print parameters if personal_names is available
                    if (selectedVideo.personal_names) {
                      url += `&custom-print=Yes&custom-print-name=${encodeURIComponent(selectedVideo.personal_names)}`;
                    }

                    window.open(url, '_blank');


                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px'
                  }}
                >
                  <span>{user && selectedVideo.user_id === user.id ? '🛍️ Buy Now' : '🎨 Create your own AI video with this t-shirt'}</span>
                  {user && selectedVideo.user_id === user.id && selectedVideo.size_detection_data && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      {(() => {
                        try {
                          const sizeCounts = {};
                          return Object.entries(sizeCounts)
                            .sort((a, b) => {
                              const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6 };
                              return (sizeOrder[a[0]] || 99) - (sizeOrder[b[0]] || 99);
                            })
                            .map(([size, qty]) => (
                              <span key={size}>
                                {size}:{qty}
                              </span>
                            ));
                        } catch (error) {
                          return <span>M:{selectedVideo.person_count}</span>;
                        }
                      })()}
                    </div>
                  )}
                  {user && selectedVideo.user_id === user.id && Object.keys(sizeQuantities).length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      {Object.entries(sizeQuantities)
                        .sort((a, b) => (sizeOrder[a[0]] || 99) - (sizeOrder[b[0]] || 99))
                        .map(([size, qty]) => (
                          <span key={size}>
                            {size}:{qty}
                          </span>
                        ))}
                    </div>
                  )}
                </button>
              )}
            </div>

            {user && selectedVideo.user_id === user.id && (
<div style={{
              background: '#f7fafc',
              padding: '30px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                marginBottom: '15px', 
                color: '#1a202c',
                textAlign: 'center',
                fontSize: '20px'
              }}>
                📱 Share Instantly on Social Media
              </h3>
              <p style={{
                textAlign: 'center',
                color: '#718096',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                One-tap sharing with auto-generated captions
              </p>

              {/* Social Media Buttons */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('tiktok', selectedVideo) : handleShare('tiktok', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#000000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  🎵 TikTok
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('instagram', selectedVideo) : handleShare('instagram', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  📷 Instagram
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('facebook', selectedVideo) : handleShare('facebook', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#1877F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  👥 Facebook
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('twitter', selectedVideo) : handleShare('twitter', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#1DA1F2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  ✖️ X (Twitter)
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('whatsapp', selectedVideo) : handleShare('whatsapp', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#25D366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  💬 WhatsApp
                </button>

                <button
                  onClick={() => activeTab === 'image' ? handleShareImage('copy', selectedVideo) : handleShare('copy', selectedVideo)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#6B7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  📋 Copy Link
                </button>
              </div>
            </div>
            )}
            {/* Share Instantly on Social Media Section */}
            
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-videos-container">
      <Helmet>
        <title>{selectedVideo ? `${selectedVideo.shirt_info?.name || 'AI Fashion Video'} - PakaiNow` : 'My Videos - PakaiNow'}</title>
        <meta name="description" content={selectedVideo ? `Check out this amazing AI-generated fashion video created with PakaiNow! #PakaiNowAI #MyAITee #WearTheFuture` : 'View your AI-generated fashion videos on PakaiNow'} />
        
        {/* OpenGraph meta tags for social sharing */}
        <meta property="og:title" content={selectedVideo ? `${selectedVideo.shirt_info?.name || 'AI Fashion Video'} - PakaiNow` : 'My Videos - PakaiNow'} />
        <meta property="og:description" content={selectedVideo ? `Check out this amazing AI-generated fashion video created with PakaiNow! #PakaiNowAI #MyAITee #WearTheFuture` : 'View your AI-generated fashion videos on PakaiNow'} />
        {selectedVideo && selectedVideo.watermarked_video_url && (
          <>
            <meta property="og:video" content={selectedVideo.watermarked_video_url} />
            <meta property="og:video:secure_url" content={selectedVideo.watermarked_video_url} />
            <meta property="og:video:type" content="video/mp4" />
            <meta property="og:video:width" content="1080" />
            <meta property="og:video:height" content="1920" />
          </>
        )}
        {selectedVideo && selectedVideo.watermarked_image_url && (
          <>
            <meta property="og:image" content={selectedVideo.watermarked_image_url} />
            <meta property="og:image:secure_url" content={selectedVideo.watermarked_image_url} />
            <meta property="og:image:width" content="1080" />
            <meta property="og:image:height" content="1920" />
            <meta property="og:image:alt" content={`${selectedVideo.shirt_info?.name || 'AI Fashion Video'} - PakaiNow`} />
          </>
        )}
        <meta property="og:type" content={selectedVideo && selectedVideo.watermarked_video_url ? "video.other" : "website"} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="PakaiNow" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content={selectedVideo && selectedVideo.watermarked_video_url ? "player" : "summary_large_image"} />
        <meta name="twitter:title" content={selectedVideo ? `${selectedVideo.shirt_info?.name || 'AI Fashion Video'} - PakaiNow` : 'My Videos - PakaiNow'} />
        <meta name="twitter:description" content={selectedVideo ? `Check out this amazing AI-generated fashion video created with PakaiNow! #PakaiNowAI #MyAITee #WearTheFuture` : 'View your AI-generated fashion videos on PakaiNow'} />
        {selectedVideo && selectedVideo.watermarked_image_url && (
          <meta name="twitter:image" content={selectedVideo.watermarked_image_url} />
        )}
        {selectedVideo && selectedVideo.watermarked_video_url && (
          <>
            <meta name="twitter:player" content={selectedVideo.watermarked_video_url} />
            <meta name="twitter:player:width" content="1080" />
            <meta name="twitter:player:height" content="1920" />
          </>
        )}
      </Helmet>
      <div className="my-videos-header">
        <h1>🎬 My Videos</h1>
        <p>Manage and view all your generated videos</p>
      </div>

      {videos.length === 0 ? (
        <div className="no-videos">
          <div className="no-videos-icon">🎥</div>
          <h3>No videos yet</h3>
          <p>You haven't generated any videos yet. Start creating your first video!</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="btn btn-primary"
          >
            Create Your First Video
          </button>
        </div>
      ) : (
        <div className="videos-grid">
          {videos.map((video) => (
            <div 
              key={video.id} 
              className="video-card clickable" 
              onClick={() => handleCardClick(video)}
            >
              <div className="video-thumbnail">
                {video.watermarked_image_url ? (
                  <img 
                    src={video.watermarked_image_url} 
                    alt="Video thumbnail"
                    className="thumbnail-image"
                  />
                ) : (
                  <div className="video-placeholder">
                    <span className="video-icon">🎬</span>
                  </div>
                )}
              </div>
              
              <div className="video-info">
                <div className="video-status">
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(video.status) }}
                  >
                    {video.status || 'Unknown'}
                  </span>
                </div>
                
                <div className="video-details">
                  <p className="video-date">
                    Created: {formatDate(video.created_at)}
                  </p>
                  
                  {video.person_count > 0 && (
                    <p className="person-count">
                      👥 {video.person_count} person(s)
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="my-videos-footer">
        <button 
          onClick={fetchMyVideos}
          className="btn btn-outline"
        >
          🔄 Refresh Videos
        </button>
      </div>
    </div>
  );
};

export default MyVideos;